/** Link from a matched field to where that record is edited.
 *
 * Always opens in a NEW TAB. Find & Replace is a review surface: an operator
 * checking a diff needs the record beside the diff, not instead of it — and
 * navigating away mid-review costs the pinned result set, the selection, and any
 * dry run they had already approved.
 *
 * Renders plain text when there is no link. Four kinds (notification strings,
 * standard templates, template blocks, rulesets) have no console page at all, so
 * this is a real state rather than a defensive branch — a link that 404s would
 * be worse than none.
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Link as MuiLink, Tooltip } from '@mui/material';

export interface RecordLinkProps {
  /** Console-relative path, or null when the kind has no console page. */
  link: string | null | undefined;
  label: string;
  /** Shown in the tooltip so the destination is legible before clicking. */
  kindLabel?: string | null | undefined;
}

export function RecordLink({ link, label, kindLabel }: RecordLinkProps) {
  if (!link) {
    return (
      <Tooltip describeChild title="This record has no console page — edit it in the dashboard or admin.">
        <Box component="span" sx={{ color: 'text.primary' }}>
          {label}
        </Box>
      </Tooltip>
    );
  }
  return (
    // `describeChild`: without it MUI makes the tooltip the child's accessible
    // NAME, so a screen reader would announce every one of these links as
    // "Open notification in a new tab" and drop the record's identity.
    <Tooltip describeChild title={`Open ${kindLabel ? `${kindLabel.toLowerCase()} ` : ''}in a new tab`}>
      <MuiLink
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        // The grid opens a row's detail panel on click; without this a link
        // click would also toggle that behind the new tab.
        onClick={(e) => e.stopPropagation()}
        underline="hover"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {label}
        <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0, opacity: 0.7 }} />
      </MuiLink>
    </Tooltip>
  );
}
